<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else class="quick-blast font-sans text-slate-700">
        <step-form-heading :icon="faRocketLaunch">
            Quick Blast

            <template v-slot:description> The fastest way to send a single message to multiple people. </template>
        </step-form-heading>

        <quick-blast-group-status-alert
            v-if="!currentGroupCanSend"
            class="mt-8"
            :status="currentGroup?.status"
        ></quick-blast-group-status-alert>

        <vx-sticky-nav
            class="mt-8"
            v-model="currentStep"
            :sections="sections"
            :navClasses="`${success ? 'hidden' : 'flex'}`"
        >
            <div class="relative max-w-(--breakpoint-md) mx-auto">
                <div
                    class="flex transition-all duration-500 ease-[cubic-bezier(0.5,0.5,0.3,1)]"
                    :class="{
                        'scale-[.80]': success,
                    }"
                >
                    <quick-blast-type
                        :modelValue="campaign.type"
                        @update:modelValue="changeCampaignType($event)"
                        :active="currentStep == 'type'"
                        @update:active="$event ? (currentStep = 'type') : (currentStep = null)"
                        @continue="continueFromType()"
                        :success="success"
                        :disabled="!!groupMessageRulesError || !currentGroupCanSend"
                    ></quick-blast-type>
                </div>
                <div
                    v-if="currentOrgUnit?.id"
                    class="flex transition-all duration-500 ease-[cubic-bezier(0.5,0.5,0.3,1)]"
                    :class="{
                        'scale-[.85] -mt-9': success,
                        'mt-2 lg:mt-8': !success,
                    }"
                >
                    <quick-blast-from
                        v-model="groups"
                        :orgUnitId="currentOrgUnit?.id"
                        :active="currentStep == 'from'"
                        @update:active="$event ? (currentStep = 'from') : (currentStep = null)"
                        @continue="continueFromFrom($event)"
                        :success="success"
                    ></quick-blast-from>
                </div>
                <div
                    class="flex transition-all duration-500 ease-[cubic-bezier(0.5,0.5,0.3,1)]"
                    :class="{
                        'scale-[.85] -mt-9': success,
                        'mt-2 lg:mt-8': !success,
                    }"
                >
                    <quick-blast-to
                        v-model="people"
                        :groupIds="currentOrgUnit?.id ? groups.map((group) => group.group_id) : undefined"
                        :active="currentStep == 'to'"
                        :campaignType="campaign.type"
                        @update:active="$event ? (currentStep = 'to') : (currentStep = null)"
                        @continue="continueFromTo($event)"
                        :success="success"
                    ></quick-blast-to>
                </div>
                <div
                    class="flex transition-all duration-500 ease-[cubic-bezier(0.5,0.5,0.3,1)]"
                    :class="{
                        'scale-[.90] -mt-8': success,
                        'mt-2 lg:mt-8': !success,
                    }"
                >
                    <quick-blast-what
                        v-model="message"
                        :active="currentStep == 'what'"
                        :campaignType="campaign.type"
                        @update:active="$event ? (currentStep = 'what') : (currentStep = null)"
                        @continue="continueFromWhat"
                        :loading="validatingWhat || upsertingCampaign"
                        :success="success"
                        :externalContactLists="people.type === 'external-contact-lists' ? people.data : []"
                        :errors="bodyErrors"
                        @snippetUsed="snippetUsed = $event"
                    ></quick-blast-what>
                </div>
                <div
                    class="flex transition-all duration-500 ease-[cubic-bezier(0.5,0.5,0.3,1)]"
                    :class="{
                        'scale-[.95] -mt-7': success,
                        'mt-2 lg:mt-8': !success,
                    }"
                >
                    <quick-blast-when
                        v-model="scheduledAt"
                        v-model:reserveDate="reserveDate"
                        v-model:reserveDateReason="reserveDateReason"
                        v-model:blockOtherType="blockOtherType"
                        :active="currentStep == 'when'"
                        @update:active="$event ? (currentStep = 'when') : (currentStep = null)"
                        @continue="continueFromWhen"
                        :success="success"
                        :loading="upsertingCampaign || estimationLoading"
                        :campaignType="campaign.type"
                    ></quick-blast-when>
                </div>
            </div>
            <div
                class="max-w-(--breakpoint-md) mx-auto flex relative transition-all duration-500 ease-[cubic-bezier(0.5,0.5,0.3,1)]"
                :class="{
                    'scale-[1.02] -mt-6': success,
                    'mt-2 lg:mt-8': !success,
                }"
            >
                <div class="w-full">
                    <quick-blast-launch
                        :orgUnitId="currentOrgUnit?.id"
                        :groupId="currentGroup?.group_id"
                        v-model:name="campaign.name"
                        @save:name="saveName()"
                        :active="currentStep === 'launch'"
                        :mediaUrl="message.mediaUrl"
                        :mediaContentType="message.mediaContentType"
                        :bodyMessage="previewBodyMessage"
                        :success="success"
                        @submit="submit()"
                        :submitting="submitting"
                        :estimation="estimation"
                        :estimatedCost="estimatedCost"
                        :groupIds="groups.map((group) => group.group_id)"
                        :scheduled="scheduledAt !== 'now'"
                        :disableLockedSnippets="message.snippetId && !snippetUsed?.is_locked"
                        :snippetId="message.snippetId"
                        :snippetFieldBindings="message.snippetFieldBindings"
                    ></quick-blast-launch>
                    <quick-blast-follow-up
                        v-if="currentStep === 'launch' && success && onCorporate()"
                        :originalCampaignId="campaign.id"
                        :originalCampaignName="campaign.name"
                        :originalCampaignType="campaign.type"
                        :originalCampaignCategory="campaign.category"
                        :originalScheduledAt="liveAtDate"
                        :originalMessage="message"
                    ></quick-blast-follow-up>
                </div>
            </div>
        </vx-sticky-nav>
    </form-container>
</template>

<script setup>
import { faRocketLaunch } from '@fortawesome/pro-solid-svg-icons';
import { VxStickyNav, VxSwal } from '@voxie/frontend-components';
import { reactive, ref, watch, computed, inject, onMounted } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { actionsClient, campaignsClient, contactsClient, reservedDatesClient } from '~/services';
import { useMessageRules } from './useMessageRules';
import PageLoading from '../general/PageLoading.vue';
import { formatTz, getClientTz } from '~/utils/date';
import QuickBlastLaunch from './QuickBlastLaunch.vue';
import QuickBlastType from './QuickBlastType.vue';
import QuickBlastWhat from './QuickBlastWhat.vue';
import QuickBlastWhen from './QuickBlastWhen.vue';
import QuickBlastFrom from './QuickBlastFrom.vue';
import QuickBlastTo from './QuickBlastTo.vue';
import QuickBlastFollowUp from './QuickBlastFollowUp.vue';
import QuickBlastGroupStatusAlert from './QuickBlastGroupStatusAlert.vue';
import {
    AUDIENCE_TYPE_MARKETING,
    AUDIENCE_TYPE_TRANSACTIONAL,
    buildCampaignMessage,
    extractCampaignMessageErrors,
} from '~/constants/campaign';
import StepFormHeading from '../general/StepFormHeading.vue';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import FormContainer from '../general/FormContainer.vue';
import { catchRequestError } from '~/utils/request';
import { HTTP_BAD_REQUEST, HTTP_CONFLICT, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import { getSnippetPreviewMessage } from '~/components/quick-blast/snippets';
import { useValidateMessageStandards } from '~/components/quick-blast/useValidateMessageStandards';

const FEATURES = inject('FEATURES');

const props = defineProps({
    defaultCampaignType: {
        type: String,
    },
    defaultTo: {
        type: String,
    },
    defaultExternalContactLists: {
        type: Array,
    },
    defaultSnippetId: {
        type: Number,
    },
});

const teamId = Spark.state.currentTeam.id;
let campaignType = FEATURES.transactional_default ? AUDIENCE_TYPE_TRANSACTIONAL : AUDIENCE_TYPE_MARKETING;
if (props.defaultCampaignType) {
    campaignType = props.defaultCampaignType;
}

const campaign = reactive({
    name: '',
    type: campaignType,
    everyone: false,
});

const submitting = ref(false);
const upsertingCampaign = ref(false);

const toasted = useToasted();
const { onCorporate, currentGroup, currentGroupCanSend, currentOrgUnit } = useCurrentGroup();
const {
    getMessageRules,
    loading: rulesLoading,
    error: groupMessageRulesError,
    lockedSnippetsEnabled,
} = useMessageRules();

const estimation = ref(null);
const estimatedCost = ref(null);
const estimationLoading = ref(false);

const steps = ['type', 'to', 'what', 'when', 'launch'];
const currentStep = ref('type');
const success = ref(false);

const snippetUsed = ref();

const loading = computed(() => rulesLoading.value);
const previewBodyMessage = computed(() => {
    if (message.value.snippetId && snippetUsed.value?.is_locked && message.value.snippetFieldBindings.length > 0) {
        return getSnippetPreviewMessage(message.value.body, message.value.snippetFieldBindings);
    }

    return message.value.body;
});

const sections = computed(() => {
    const currentStepIndex = steps.indexOf(currentStep.value);
    const items = [{ id: 'type', label: 'Type' }];

    if (currentOrgUnit.value?.id) {
        items.push({ id: 'from', label: 'From', disabled: currentStepIndex < steps.indexOf('from') });
    }

    return items.concat([
        { id: 'to', label: 'To', disabled: currentStepIndex < steps.indexOf('to') },
        { id: 'what', label: 'What', disabled: currentStepIndex < steps.indexOf('what') },
        { id: 'when', label: 'When', disabled: currentStepIndex < steps.indexOf('when') },
        { id: 'launch', label: 'Launch', disabled: currentStepIndex < steps.indexOf('launch') },
    ]);
});

const message = ref({
    body: '',
    mediaUrl: null,
    mediaContentType: null,
    snippetId: props.defaultSnippetId || null,
    snippetFieldBindings: [],
});

const scheduledAt = ref();
const reserveDate = ref(false);
const reserveDateReason = ref('');
const blockOtherType = ref(false);
const liveAtDate = computed(() => dayjs.utc(campaign.live_at).toDate());

const groups = ref([]);

const people = ref({
    type: props.defaultTo ? props.defaultTo : 'segments',
    data: props.defaultExternalContactLists ? props.defaultExternalContactLists : [],
});

const { bodyErrors, validate: validateMessageStandards } = useValidateMessageStandards({
    campaignType: () => campaign.type,
    messageBody: () => message.value.body,
    messageMediaUrl: () => message.value.mediaUrl,
    messageMediaContentType: () => message.value.mediaContentType,
    messageSnippetId: () => message.value.snippetId,
    messageSnippetFieldBindings: () => message.value.snippetFieldBindings || [],
    snippetIsLocked: () => Boolean(snippetUsed.value?.is_locked),
    audienceSegmentIds: () => {
        if (people.value.type === 'segments') {
            return people.value.data.map((item) => item.id);
        }
        return [];
    },
    audienceExternalContactListIds: () => {
        if (people.value.type === 'external-contact-lists') {
            return people.value.data.map((item) => item.id);
        }
        return [];
    },
    audienceGroupIds: () => groups.value.map((group) => group.group_id),
});

const campaignSave = async (campaignPayload) => {
    if (onCorporate()) {
        return campaignsClient.campaignSave(teamId, campaignPayload);
    } else if (currentOrgUnit.value?.id) {
        return campaignsClient.orgUnitCampaignSave(teamId, currentOrgUnit.value.id, campaignPayload);
    } else {
        return campaignsClient.groupCampaignSave(teamId, currentGroup.value.group_id, campaignPayload);
    }
};

const campaignCapacity = async () => {
    if (onCorporate()) {
        return campaignsClient.campaignCapacity(teamId, campaign.id);
    } else if (currentOrgUnit.value?.id) {
        return campaignsClient.orgUnitCampaignCapacity(teamId, currentOrgUnit.value.id, campaign.id);
    } else {
        return campaignsClient.groupCampaignCapacity(teamId, currentGroup.value.group_id, campaign.id);
    }
};

const campaignChangeStatus = async () => {
    const status = scheduledAt.value === 'now' ? 'published' : 'scheduled';

    if (onCorporate()) {
        await campaignsClient.changeStatus(teamId, campaign.id, status);
    } else if (currentOrgUnit.value?.id) {
        await campaignsClient.orgUnitCampaignChangeStatus(teamId, currentOrgUnit.value.id, campaign.id, status);
    } else {
        await campaignsClient.groupCampaignChangeStatus(teamId, currentGroup.value.group_id, campaign.id, status);
    }
};

const generateDefaultName = (dayjsInstance) => {
    dayjsInstance = dayjsInstance || dayjs().tz(getClientTz());
    return 'Quick Blast - ' + dayjsInstance.format('MM/DD/YYYY h:mm:ss a') + ' ' + formatTz(dayjsInstance.format('z'));
};

const buildAudienceIds = () => {
    const ids = {
        segment_ids: [],
        external_contact_list_ids: [],
    };

    if (people.value.type === 'segments') {
        ids.segment_ids = people.value.data.map((item) => item.id);
    } else if (people.value.type === 'external-contact-lists') {
        ids.external_contact_list_ids = people.value.data.map((item) => item.id);
    }

    return ids;
};

const upsertCampaign = async () => {
    if (upsertingCampaign.value) {
        throw new Error('The campaign is still being saved.');
    }

    campaign.name = !campaign.name || !campaign.live_at ? generateDefaultName() : campaign.name;
    campaign.duration_type = 'one_time';
    campaign.remove_on_response = 1;
    campaign.remove_on_link_click = 1;

    const campaignMessage = buildCampaignMessage({
        snippetId: message.value.snippetId,
        snippetIsLocked: Boolean(snippetUsed.value?.is_locked),
        fieldBindings: message.value.snippetFieldBindings || [],
        body: message.value.body,
        mediaUrl: message.value.mediaUrl,
        mediaContentType: message.value.mediaContentType,
        status: 'published',
    });

    campaign.campaign_messages = [campaignMessage];

    const campaignData = { ...campaign };
    const audienceIds = buildAudienceIds();
    campaignData.segment_ids = audienceIds.segment_ids;
    campaignData.external_contact_list_ids = audienceIds.external_contact_list_ids;

    if (currentOrgUnit.value?.id) {
        campaignData.group_ids = groups.value.map((group) => group.group_id);
    }

    try {
        upsertingCampaign.value = true;
        bodyErrors.value = [];
        const response = await campaignSave(campaignData);
        campaign.id = response.data.id;
        campaign.category = response.data.category;
    } finally {
        upsertingCampaign.value = false;
    }
};

// clears the segments when type changes, this
// happens because the segments must have the
// same audience type as the campaign type
watch(
    () => campaign.type,
    () => {
        // only clears in case the campaign has been created
        // and the people selected is of type segments
        if (campaign.id && people.value.type === 'segments') {
            campaign.segment_ids = [];
            people.value = {
                type: 'segments',
                data: [],
            };
        }
    }
);

const changeCampaignType = (campaignType) => {
    const previousCampaignType = campaign.type;

    if (!lockedSnippetsEnabled('marketing')) {
        // do nothing if not enabled
        campaign.type = campaignType;
        return;
    }

    // when changing the type we need to clear out the message data when using locked snippets
    if (
        campaignType === previousCampaignType ||
        (!message.value.body?.length && !message.value.snippetFieldBindings?.length)
    ) {
        campaign.type = campaignType;
        return;
    }

    VxSwal.confirmation(
        'Changing Type',
        `Changing the type while on <strong>Locked Snippets</strong> will make you lose your message. Do you want to change the type from <strong>${previousCampaignType}</strong> to <strong>${campaignType}</strong>?`,
        'danger'
    ).then((confirmed) => {
        if (!confirmed) {
            return;
        }

        campaign.type = campaignType;
        message.value.body = '';
        message.value.mediaUrl = undefined;
        message.value.mediaContentType = undefined;
        message.value.snippetId = undefined;
        message.value.snippetFieldBindings = [];
    });
};

const continueFromType = async () => {
    if (!currentGroupCanSend.value) {
        return;
    }

    const nextStep = currentOrgUnit.value?.id ? 'from' : 'to';

    if (campaign.id) {
        upsertCampaign()
            .then(() => {
                currentStep.value = nextStep;
            })
            .catch((err) => toasted.error(catchRequestError(err)));
    } else {
        currentStep.value = nextStep;
    }
};

if (props.defaultCampaignType) {
    // If the "type" is preselected, let's move to the next step
    continueFromType();
}

const clearEstimation = () => {
    estimation.value = null;
    estimatedCost.value = null;
};

const fetchEstimation = async () => {
    estimationLoading.value = true;
    try {
        if (onCorporate() && people.value.type === 'contacts') {
            const contactsCount = await contactsClient.contactsExpectedCount(
                teamId,
                campaign.type,
                people.value.data.map((item) => item.id)
            );

            estimation.value = contactsCount.data.count;
        } else if (
            people.value.type === 'segments' ||
            people.value.type === 'everyone' ||
            people.value.type === 'external-contact-lists'
        ) {
            const campaignAudience = await campaignCapacity();

            estimation.value = campaignAudience.data.audience_size;
            estimatedCost.value = campaignAudience.data.estimated_cost;
        }
    } catch (e) {
        console.error(e);
    } finally {
        estimationLoading.value = false;
    }
};

const continueFromFrom = () => {
    currentStep.value = 'to';
};

const continueFromTo = async () => {
    campaign.everyone = people.value.type === 'everyone';
    if (campaign.id) {
        upsertCampaign()
            .then(() => {
                currentStep.value = 'what';
            })
            .catch((err) => toasted.error(catchRequestError(err)));
    } else {
        currentStep.value = 'what';
    }
};

const continueFromWhen = async () => {
    clearEstimation();

    const liveAt = scheduledAt.value === 'now' ? dayjs().tz(getClientTz()) : dayjs(scheduledAt.value).tz(getClientTz());

    campaign.name = !campaign.live_at ? generateDefaultName(liveAt) : campaign.name;
    campaign.live_at = toUTCDateTimeFormat(liveAt);

    try {
        await upsertCampaign();

        if (FEATURES.contact_groups && onCorporate() && reserveDate.value && scheduledAt.value !== 'now') {
            const dateStr = dayjs(scheduledAt.value).tz(getClientTz()).format('YYYY-MM-DD');
            try {
                await reservedDatesClient.createReservedDate(
                    { teamId },
                    {
                        date: dateStr,
                        marketing: campaign.type === AUDIENCE_TYPE_MARKETING || blockOtherType.value,
                        transactional: campaign.type !== AUDIENCE_TYPE_MARKETING || blockOtherType.value,
                        reason: reserveDateReason.value || null,
                    }
                );
            } catch (e) {
                if (e?.response?.status !== HTTP_CONFLICT) {
                    toasted.error(catchRequestError(e));
                }
            }
        }

        await fetchEstimation();
        currentStep.value = 'launch';
    } catch (err) {
        toasted.error(catchRequestError(err));
    }
};

const validatingWhat = ref(false);
const continueFromWhat = async () => {
    if (validatingWhat.value) {
        return;
    }
    validatingWhat.value = true;
    const validationPassed = await validateMessageStandards();
    validatingWhat.value = false;
    if (!validationPassed) {
        return;
    }

    upsertCampaign()
        .then(() => {
            currentStep.value = 'when';
        })
        .catch((err) => {
            if (err?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
                const errors = extractCampaignMessageErrors(err);
                if (errors.length > 0) {
                    bodyErrors.value = errors;
                    return;
                }
            }
            toasted.error(catchRequestError(err));
        });
};

const submitFailed = (message) => {
    submitting.value = false;
    toasted.error(message);
};

const saveName = () => {
    upsertCampaign().catch((err) => toasted.error(catchRequestError(err)));
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    if (onCorporate() && people.value.type === 'contacts') {
        try {
            await campaignsClient.addContactsToCampaign(
                teamId,
                campaign.id,
                people.value.data.map((item) => item.id)
            );
        } catch (e) {
            console.error(e);
            submitFailed('It was not possible to add the contacts to the campaign. Please try again.');
            return;
        }
    }

    try {
        await campaignChangeStatus();

        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });

        success.value = true;

        if (
            message.value.snippetId &&
            (snippetUsed.value?.is_locked || message.value.body.includes(snippetUsed.value?.body))
        ) {
            actionsClient.send('snippet_used');
        }
    } catch (e) {
        console.error(e);
        submitFailed(
            e?.response?.status === HTTP_BAD_REQUEST && e.response.data?.message
                ? e.response.data.message
                : 'It was not possible to schedule this message. Please try again.'
        );
    }
};

onMounted(() => {
    if (!onCorporate()) {
        getMessageRules({ orgUnitId: currentOrgUnit.value?.id, groupId: currentGroup.value?.group_id });
    }
});
</script>

<style scoped>
@reference "@css/style.css";

.quick-blast :deep(.vx-message-builder__toolbar) {
    @apply bg-white border-b border-x-0 border-t-0 border-solid border-slate-300;
}
.quick-blast :deep(.vx-message-builder) {
    @apply border border-solid border-slate-300 shadow-none rounded-xl overflow-hidden;
}
.quick-blast :deep(.vx-mb-control__icon) {
    @apply text-slate-400;
}
.quick-blast :deep(.vx-vertical-divider) {
    @apply h-4 bg-slate-200;
}
.quick-blast :deep(.message-builder__body) {
    @apply font-sans text-slate-700 font-normal text-base;
}
.quick-blast :deep(.vx-mb-control-attachment__text),
.quick-blast :deep(.vx-input__input),
.quick-blast :deep(.vx-button__text),
.quick-blast :deep(.emoji-mart-category-label),
.quick-blast :deep(.emoji-mart-search input),
.quick-blast :deep(.vx-mb-control.vx-mb-control-variables .popper),
.quick-blast :deep(.vx-mb-control.vx-mb-control-templates .popper),
.quick-blast :deep(.message-builder__body) {
    @apply font-sans;
}
</style>
